/**
 * Input-log replays. Stored as run-length (tick, word) change events, so a
 * 3-minute run is a few hundred bytes. Used by QA tooling now and by
 * server-side verification in Phase 6.
 */
export interface ReplayEvent {
  /** Tick index at which the input word changes to `word`. */
  tick: number;
  word: number;
}

export interface Replay {
  /** Version of the replay container itself. */
  version: 1;
  /** Seed the sim was created with. */
  seed: number;
  /** Hash of the level data string, to refuse replays against edited levels. */
  levelHash: number;
  events: ReplayEvent[];
  /** Final tick count and state hash for verification. */
  finalTick: number;
  finalStateHash: number;
}

/** FNV-1a 32-bit hash of a level's serialized text. Client stamps it into
 * replays; the server refuses replays whose hash mismatches the uploaded
 * level (no verifying against an edited copy). */
export function hashLevelText(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export class ReplayRecorder {
  private events: ReplayEvent[] = [];
  private lastWord = 0;

  record(tick: number, word: number): void {
    if (word !== this.lastWord) {
      this.events.push({ tick, word });
      this.lastWord = word;
    }
  }

  finish(seed: number, levelHash: number, finalTick: number, finalStateHash: number): Replay {
    return {
      version: 1,
      seed,
      levelHash,
      events: this.events,
      finalTick,
      finalStateHash,
    };
  }

  reset(): void {
    this.events = [];
    this.lastWord = 0;
  }
}

/** Streams input words tick-by-tick out of a recorded replay. */
export class ReplayPlayer {
  private idx = 0;
  private word = 0;

  constructor(private readonly replay: Replay) {}

  wordAt(tick: number): number {
    while (this.idx < this.replay.events.length) {
      const ev = this.replay.events[this.idx];
      if (ev === undefined || ev.tick > tick) break;
      this.word = ev.word;
      this.idx++;
    }
    return this.word;
  }
}
